import { hasUnsavedChanges, setSwitchDraftTracking } from './api'
import { getMe, type MembershipRound } from './me'
import { hasPendingPhotoDrafts } from './photo-note'
import { hasPendingVoiceDrafts } from './voice-note'
import { el, toast, setConfirmationRound } from './ui'

const NOTICE = 'roundly:round-switched'
let busy = false
const changedFields = new Set<HTMLElement>()
let watchingDrafts = false
function watchDrafts(): void {
  if (watchingDrafts) return
  watchingDrafts = true
  setSwitchDraftTracking(true)
  // Raw form edits block switching. Once their save is durably queued they belong to the queue instead.
  const changed = (e: Event): void => {
    const field = e.target as HTMLElement
    if (field?.closest('form, dialog, #screen') && !field?.matches('input[type="search"], input[type="file"], [data-filter]') && !field.closest('.screen-bar, .view-controls, .filters, .day-strip')) changedFields.add(field)
  }
  document.addEventListener('input', changed, true)
  document.addEventListener('change', changed, true)
  const saves = new Map<string, Map<HTMLElement, string>>()
  const value = (field: HTMLElement): string => {
    const input = field as HTMLInputElement
    return `${input.value}|${input.checked}`
  }
  window.addEventListener('roundly:save-started', (event) => {
    const active = document.activeElement as HTMLElement | null
    const scope = active?.closest('form, dialog, #screen')
    if (!scope) return
    saves.set((event as CustomEvent).detail.key, new Map([...changedFields].filter((f) => scope.contains(f)).map((f) => [f, value(f)])))
  })
  window.addEventListener('roundly:save-stored', (event) => {
    const key = (event as CustomEvent).detail.key
    for (const [field, before] of saves.get(key) ?? []) if (value(field) === before) changedFields.delete(field)
    saves.delete(key)
  })
  window.addEventListener('hashchange', () => { changedFields.clear(); saves.clear() })
  window.addEventListener('dropby:identity-changed', (e) => {
    if (document.querySelector('dialog[open]') || [...changedFields].some((field) => field.isConnected)) {
      if ((e as CustomEvent).detail) (e as CustomEvent).detail.formHeld = true
      e.preventDefault()
    }
  })
}
export function switchBlocker(): string | null {
  if (!navigator.onLine) return 'Switching rounds needs signal'
  if (hasPendingPhotoDrafts()) return 'Save or close the photo first'
  if (hasPendingVoiceDrafts()) return 'Save or close the voice note first'
  if (hasUnsavedChanges() || document.querySelector('dialog[open]') || [...changedFields].some((field) => field.isConnected)) return 'Save or close the form first'
  return null
}
async function switchRound(round: MembershipRound): Promise<void> {
  if (busy) return
  const blocked = switchBlocker()
  if (blocked) { toast(blocked); return }
  busy = true
  const app = document.querySelector<HTMLElement>('#app')
  if (app) app.inert = true
  try {
    const res = await fetch('/api/me/round', { method: 'POST', credentials: 'same-origin',
      headers: { 'content-type': 'application/json' }, body: JSON.stringify({ businessId: round.businessId }), signal: AbortSignal.timeout(15_000) })
    if (!res.ok) { toast('Could not switch rounds. Try again.'); return }
    try { sessionStorage.setItem(NOTICE, JSON.stringify({ businessId: round.businessId, shortName: round.shortName, colour: round.colour })) } catch { /* switching still works */ }
    location.reload()
  } catch { toast('Switching rounds needs signal') } finally { busy = false; if (app) app.inert = false }
}
function dot(colour: string): HTMLElement {
  const d = el('span', { class: 'round-dot', 'aria-hidden': 'true' })
  if (/^#[0-9a-f]{6}$/i.test(colour)) d.style.backgroundColor = colour
  return d
}
function sharedShortName(rounds: MembershipRound[], round: MembershipRound): boolean {
  return rounds.some((other) => other.businessId !== round.businessId && other.shortName === round.shortName)
}
function currentIndicator(): HTMLElement {
  return el('span', {}, el('span', { 'aria-hidden': 'true' }, '✓'), el('span', { class: 'sr-only' }, '(current round)'))
}
function listChooser(rounds: MembershipRound[]): HTMLElement[] {
  return rounds.map((round) => {
    const shared = sharedShortName(rounds, round)
    const row = el(round.current ? 'div' : 'button', {
      class: `round-row${round.current ? ' round-current' : ''}`,
      ...(round.current ? { 'aria-current': 'true' } : { type: 'button', role: 'menuitem' }),
    }, dot(round.colour), el('span', { class: 'round-names' }, el('span', {}, round.shortName),
      el('small', {}, round.current ? (shared ? `You're in this one · ${round.name}` : "You're in this one") : (shared ? round.name : 'Tap to switch'))),
      round.current ? currentIndicator() : '')
    if (!round.current) row.addEventListener('click', () => void switchRound(round))
    return row
  })
}

export function bindRoundSwitcher(button: HTMLButtonElement, menu: HTMLElement): void {
  let block: HTMLElement | null = null
  const paint = (): void => {
    block?.remove()
    block = null
    const rounds = getMe()?.rounds ?? []
    const current = rounds.find((r) => r.current)
    if (rounds.length < 2 || !current) {
      if (button.style.boxShadow) button.style.boxShadow = ''
      setConfirmationRound('')
      return
    }
    watchDrafts()
    button.style.boxShadow = `0 0 0 3px ${current.colour}`
    setConfirmationRound(current.shortName)
    block = el('div', { class: 'account-rounds' }, el('div', { class: 'rounds-heading' }, 'Your rounds'))
    block.append(...listChooser(rounds))
    menu.prepend(block)
  }
  paint()
  window.addEventListener('roundly:rounds-updated', paint)
  const current = getMe()?.rounds?.find((r) => r.current)
  if (!current || (getMe()?.rounds?.length ?? 0) < 2) return
  try {
    const raw = sessionStorage.getItem(NOTICE)
    sessionStorage.removeItem(NOTICE)
    if (!raw) return
    const notice = JSON.parse(raw)
    if (notice.businessId !== current.businessId) return
    const bar = el('div', { id: 'round-switch-notice', role: 'status' }, dot(current.colour), `Now in ${current.shortName}`)
    document.body.append(bar)
    window.setTimeout(() => { bar.classList.add('fading'); window.setTimeout(() => bar.remove(), 250) }, 3_000)
  } catch { /* no notice when storage is off */ }
}
